import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronUp, ChevronDown, X } from "lucide-react"

interface Option {
  label: string
  value: number
}

interface OrderedMultiSelectProps {
  options: Option[]
  value: number[]
  onChange: (ordered: number[]) => void
  placeholder?: string
  disabled?: boolean
}

export function OrderedMultiSelect({
  options,
  value,
  onChange,
  placeholder = "Search and add...",
  disabled = false,
}: OrderedMultiSelectProps) {
  const [search, setSearch] = useState("")

  const selectedOptions = value.map((v) => options.find((o) => o.value === v)).filter(Boolean) as Option[]
  const availableOptions = options.filter(
    (o) => !value.includes(o.value) &&
      o.label.toLowerCase().includes(search.toLowerCase())
  )

  const addItem = (opt: Option) => {
    if (disabled) return
    onChange([...value, opt.value])
    setSearch("")
  }

  const removeItem = (idx: number) => {
    if (disabled) return
    const next = [...value]
    next.splice(idx, 1)
    onChange(next)
  }

  const moveUp = (idx: number) => {
    if (idx === 0 || disabled) return
    const next = [...value]
    ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
    onChange(next)
  }

  const moveDown = (idx: number) => {
    if (idx === value.length - 1 || disabled) return
    const next = [...value]
    ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
        {search && availableOptions.length > 0 && (
          <div className="absolute z-10 top-full mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-auto">
            {availableOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => addItem(opt)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="space-y-1">
          {selectedOptions.map((opt, idx) => (
            <div key={opt.value} className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
              <span className="flex-1 text-sm">{opt.label}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={idx === 0 || disabled}
                onClick={() => moveUp(idx)}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={idx === selectedOptions.length - 1 || disabled}
                onClick={() => moveDown(idx)}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive hover:text-destructive"
                disabled={disabled}
                onClick={() => removeItem(idx)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
